import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";

const UpgradePage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/payments/subscription/plans/`);

        if (!response.ok) {
          throw new Error("Failed to fetch plans");
        }
        const data = await response.json();
        console.log(data);
        setPlans(data);
      } catch (err) {
        console.error("Error fetching plans:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getMonthlyPrice = (plan) => {
    if (!plans) return 0;
    const monthlyPlan = plans.find((planName) => planName.name === plan.name && planName.billing_interval === "monthly");
    return monthlyPlan ? parseFloat(monthlyPlan.price) : 0;
  };

  const getYearlyPrice = (plan) => {
    if (!plans) return 0;
    const yearlyPlan = plans.find((planName) => planName.name === plan.name && planName.billing_interval === "yearly");
    return yearlyPlan ? parseFloat(yearlyPlan.price) : 0;
  };

  const getMonthlyPlan = (plan) => {
    if (!plans) return null;
    return plans.find((planName) => planName.name === plan.name && planName.billing_interval === "monthly");
  };

  const getYearlyPlan = (plan) => {
    if (!plans) return null;
    return plans.find((planName) => planName.name === plan.name && planName.billing_interval === "yearly");
  };

  const getFeatures = (plan) => {
    const features = plan.description.split(",");
    return features.map((feature) => feature.trim());
  };

  const handleGetStarted = (plan) => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = localStorage.getItem("token");
    // const checkout = urlParams.get("checkout");
    const planId = plan.id;

    console.log(plan);

    if (parseInt(plan.price) === 0) {
      window.location.href = "https://api.whatsapp.com/send/?phone=8801850519131&text=Hi%2C+What+can+you+do&type=phone_number&app_absent=0";
      return;
    }

    if (token && planId) {
      // Navigate to checkout page with token, plan id, and similar plans in the query params
      const params = new URLSearchParams({
        // checkout: "true", // No longer needed as we route to /checkout directly
        token: token,
        plan_id: planId,
        monthly_id: getMonthlyPlan(plan).id,
        yearly_id: getYearlyPlan(plan).id,
        billing: billingCycle,
      });

      navigate(`/checkout?${params.toString()}`);
    } else {
      console.log("redirecting to login page");
      navigate("/auth");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-brand-dark mb-6 md:mb-8">
            Pick your plan
          </h1>
          <p className="text-gray-600 text-lg">
            Choose the perfect plan for your needs
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-8 md:mb-12 mt-8">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2.5 rounded-l-xl font-semibold text-sm md:text-base transition-all duration-300 ${
                billingCycle === "monthly"
                  ? "bg-gray-600 text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-6 py-2.5 rounded-r-xl font-semibold text-sm md:text-base transition-all duration-300 ${
                billingCycle === "yearly"
                  ? "bg-gray-600 text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-cta mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading plans...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md inline-block">
              Error loading plans: {error}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        {!loading && !error && plans && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            {plans
              .filter((plan) => {
                // Filter plans based on billing cycle
                const billingInterval = billingCycle === "yearly" ? "yearly" : "monthly";
                return plan.billing_interval === billingInterval && plan.is_active;
              })
              .map((plan, index) => {
                const isYearly = billingCycle === "yearly";
                const features = getFeatures(plan);
                const planPrice = parseFloat(plan.price);
                const monthlyPrice = getMonthlyPrice(plan);
                const displayPrice = isYearly 
                  ? Math.round(planPrice / 12) 
                  : planPrice;
                const yearlyTotal = isYearly ? planPrice : null;
                const savings = isYearly && monthlyPrice > 0 ? monthlyPrice - displayPrice : 0;

              return (
                <div
                  key={index}
                  className={`bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 flex flex-col ${
                    plan.highlight
                      ? "ring-2 ring-brand-cta/50 shadow-xl scale-105 md:scale-105"
                      : "border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                  }`}
                >
                  {/* Plan Header */}
                  <div className="mb-6">
                    <h3 className="text-3xl md:text-4xl font-black text-brand-dark mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base mb-4">
                      {plan.subtitle}
                    </p>

                    {/* Price */}
                    <div className="mb-2">
                      {isYearly && monthlyPrice >= 0 ? (
                        <div className="flex items-baseline gap-2">
                          {monthlyPrice > 0 && (
                            <span className="text-2xl md:text-3xl font-black text-gray-400 line-through">
                              ${monthlyPrice}
                            </span>
                          )}
                          <span className="text-4xl md:text-5xl font-black text-brand-dark">
                            ${displayPrice}
                          </span>
                          <span className="text-gray-500 text-sm md:text-base">
                            /month
                          </span>
                        </div>
                      ) : (
                        <>
                          <span className="text-4xl md:text-5xl font-black text-brand-dark">
                            ${displayPrice}
                          </span>
                          {displayPrice > 0 && (
                            <span className="text-gray-500 text-sm md:text-base ml-2">
                              /month
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {billingCycle === "monthly" && (
                      <p className="text-gray-500 text-xs mt-1">
                        Billed monthly (${displayPrice}/month)
                      </p>
                    )}
                    {billingCycle === "yearly" && (
                      <p className="text-gray-500 text-xs mt-1">
                        Billed annually (${planPrice}/year)
                      </p>
                    )}
                  </div>

                  {/* Features List */}
                  <div className="flex-1 mb-8">
                    <ul className="space-y-3 md:space-y-4">
                      {features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start gap-3"
                        >
                          <svg
                            className="w-5 h-5 md:w-6 md:h-6 text-brand-dark flex-shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-700 text-sm md:text-base">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleGetStarted(plan)}
                    className="bg-gray-600 hover:bg-gray-800 text-gray-100 font-semibold py-3 md:py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    <span>Get Started</span>
                    <svg
                      className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradePage;
